import { CSSProperties } from "react";

interface BorderBeamProps {
  duration?: number;
  delay?: number;
  colorTo?: string;
  className?: string;
  style?: CSSProperties;
}

/**
 * Rotating beam of light along a border.
 *
 * Usage: wrap your element in a `relative overflow-hidden p-[1.5px] rounded-*` div,
 * place <BorderBeam> as a sibling (after the content). The content should have bg-card
 * so it covers the interior, leaving only the 1.5px border ring where the beam shows.
 */
export function BorderBeam({
  duration = 3,
  delay = 0,
  // Defaults to the active theme's accent. Accepts any CSS color
  // (hex or hsl(var(--token))); alpha is applied via color-mix so it
  // works with theme tokens, not just hex.
  colorTo = "hsl(var(--primary))",
  className,
  style,
}: BorderBeamProps) {
  const faded = `color-mix(in srgb, ${colorTo} 53%, transparent)`;
  return (
    <span
      aria-hidden
      className={`pointer-events-none absolute inset-0 ${className ?? ""}`}
      style={style}
    >
      <span
        style={{
          position: "absolute",
          top: "50%",
          left: "50%",
          /* Large enough that all 4 corners are inside this square as it rotates */
          width: "250%",
          paddingBottom: "250%",
          transform: "translate(-50%, -50%)",
          background: `conic-gradient(from 0deg, transparent 0deg, transparent 290deg, ${faded} 330deg, ${colorTo} 350deg, ${faded} 360deg, transparent 360deg)`,
          animation: `border-beam-rotate ${duration}s linear ${delay ? `${delay}s` : "0s"} infinite`,
        }}
      />
    </span>
  );
}
